import { escapeHtml } from './sanitize.js';
import { icon } from '../../shared/icons/render.js';

export function showToast(message, type = 'info', duration) {
  if (typeof window.showToast === 'function') {
    window.showToast(message, type, duration);
  }
  // Removed console fallback for production
}

export function setLoading(target, on = true) {
  const btn = target && target.tagName ? target : document.querySelector(target);
  if (!btn) {
    return;
  }

  const loader = btn.querySelector('.loader');
  const textEl = btn.querySelector('.checkout-btn-text, .btn-text');

  if (on) {
    if (!btn.dataset._minWidthSaved) {
      const width = Math.ceil(btn.getBoundingClientRect().width);
      if (width > 0) {
        btn.style.minWidth = `${width}px`;
        btn.dataset._minWidthSaved = '1';
      }
    }
    // Save original text and show loading text
    if (textEl && !btn.dataset._originalText) {
      btn.dataset._originalText = textEl.textContent;
    }
    if (textEl) {
      const __ = (typeof window.__ === 'function') ? window.__ : (s) => s;
      textEl.textContent = __('Wird verarbeitet...');
    }
  } else {
    if (btn.dataset._minWidthSaved) {
      btn.style.minWidth = '';
      delete btn.dataset._minWidthSaved;
    }
    // Restore original text
    if (textEl && btn.dataset._originalText) {
      textEl.textContent = btn.dataset._originalText;
      delete btn.dataset._originalText;
    }
  }

  btn.disabled = !!on;
  btn.setAttribute('aria-busy', on ? 'true' : 'false');
  if (on) {
    btn.dataset.loading = '1';
    btn.classList.add('checkout-btn--loading');
  } else {
    delete btn.dataset.loading;
    btn.classList.remove('checkout-btn--loading');
  }

  if (loader) {
    loader.classList.toggle('hidden', !on);
  }
}

/**
 * Build safe HTML for feedback boxes with proper XSS protection
 *
 * @param {string} message - The message to display (will be escaped)
 * @param {'error'|'success'|'info'|'warning'} type - Message type for styling
 * @returns {string} Safe HTML string
 */
export function buildFeedbackHtml(message, type = 'error') {
  const safeMessage = escapeHtml(message);

  const iconPaths = {
    error: 'M12 8v4l2 2',
    success: 'M8 13l3 3 5-7',
    info: 'M12 8v4m0 4h.01',
    warning: 'M12 9v2m0 4h.01',
  };

  const path = iconPaths[type] || iconPaths.error;
  const validTypes = ['error', 'success', 'info', 'warning'];
  const modifier = validTypes.includes(type) ? type : 'error';

  // Use role="alert" for errors so screen readers announce immediately
  const roleAttr = type === 'error' ? ' role="alert"' : ' role="status" aria-live="polite"';

  return `<div class="checkout-feedback checkout-feedback--${modifier} flex items-center gap-2"${roleAttr}>` +
    `<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">` +
    `<circle cx="12" cy="12" r="10"/><path d="${path}"/></svg>` +
    `<span class="flex-1">${safeMessage}</span>` +
    `<button type="button" class="checkout-feedback-dismiss" aria-label="Schlie\u00DFen" data-feedback-dismiss>` +
    `${icon('xMark', { size: 16 })}` +
    `</button></div>`;
}

/**
 * Show feedback box with HTML content
 * WARNING: Only use with trusted HTML (e.g., from buildFeedbackHtml)
 *
 * @param {HTMLElement} box - The container element
 * @param {string} html - Trusted HTML content
 */
export function showErrorBox(box, html) {
  if (!box) {
    return;
  }
  box.innerHTML = html;
  box.classList.remove('hidden');

  // Wire up dismiss button (X) for user-closeable feedback
  const dismissBtn = box.querySelector('[data-feedback-dismiss]');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      box.classList.add('hidden');
      box.innerHTML = '';
    }, { once: true });
  }

  // Auto-dismiss success messages after 5 seconds
  if (box.querySelector('.checkout-feedback--success')) {
    clearTimeout(box._autoDismissTimer);
    box._autoDismissTimer = setTimeout(() => {
      if (box.querySelector('.checkout-feedback--success')) {
        box.classList.add('hidden');
        box.innerHTML = '';
      }
    }, 5000);
  }

  const form = box.closest('.register-form, .register-guest-form');
  if (form) {
    requestAnimationFrame(() => resizeContainer(form));
  }
  // Scroll error into view if off-screen
  requestAnimationFrame(() => {
    const rect = box.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });
}

/**
 * Show feedback message with automatic XSS protection
 *
 * @param {HTMLElement} box - The container element
 * @param {string} message - Message text (will be escaped)
 * @param {'error'|'success'|'info'} type - Message type
 */
export function showFeedback(box, message, type = 'error') {
  showErrorBox(box, buildFeedbackHtml(message, type));
}

export function expandAuto(element) {
  if (!element) {
    return;
  }
  element.dataset.expanded = '1';
  element.classList.remove('hidden');

  // Use inline transition with numeric values so transitionend fires reliably
  element.style.transition = 'max-height 200ms ease, opacity 200ms ease';
  element.style.overflow = 'hidden';
  element.style.maxHeight = '0px';
  element.style.opacity = '0';

  requestAnimationFrame(() => {
    element.style.maxHeight = `${element.scrollHeight}px`;
    element.style.opacity = '1';
  });

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    element.style.maxHeight = '';
    element.style.opacity = '';
    element.style.overflow = '';
    element.style.transition = '';
    element.classList.remove('opacity-0');
    element.classList.add('opacity-100');
    element.removeEventListener('transitionend', handler);
  };
  const handler = (event) => {
    if (!['max-height', 'opacity'].includes(event.propertyName)) {
      return;
    }
    clearTimeout(fallback);
    cleanup();
  };
  const fallback = setTimeout(cleanup, 300);
  element.addEventListener('transitionend', handler);
}

export function collapseAuto(element) {
  if (!element) {
    return;
  }
  delete element.dataset.expanded;

  // Use inline transition with numeric values so transitionend fires reliably
  const height = element.scrollHeight;
  element.style.transition = 'max-height 200ms ease, opacity 200ms ease';
  element.style.overflow = 'hidden';
  element.style.maxHeight = `${height}px`;
  element.style.opacity = '1';

  requestAnimationFrame(() => {
    element.style.maxHeight = '0px';
    element.style.opacity = '0';
  });

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    element.classList.add('hidden');
    element.classList.remove('opacity-100');
    element.classList.add('opacity-0');
    element.style.maxHeight = '';
    element.style.opacity = '';
    element.style.overflow = '';
    element.style.transition = '';
    element.removeEventListener('transitionend', handler);
  };
  const handler = (event) => {
    if (!['max-height', 'opacity'].includes(event.propertyName)) {
      return;
    }
    clearTimeout(fallback);
    cleanup();
  };
  const fallback = setTimeout(cleanup, 300);
  element.addEventListener('transitionend', handler);
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function smoothExpand(element, duration = 250) {
  if (!element) {
    return;
  }

  element.classList.remove('hidden');
  element.dataset.expanded = '1';

  if (prefersReducedMotion()) {
    element.style.maxHeight = '';
    element.style.opacity = '';
    return;
  }

  element.dataset.animating = '1';

  const previousTransition = element.style.transition;
  const previousOverflow = element.style.overflow;

  element.style.transition = `max-height ${duration}ms ease, opacity ${duration}ms ease`;
  element.style.overflow = 'hidden';
  element.style.maxHeight = '0px';
  element.style.opacity = '0';

  requestAnimationFrame(() => {
    element.style.maxHeight = `${element.scrollHeight}px`;
    element.style.opacity = '1';
  });

  let cleaned = false;
  const cleanup = (event) => {
    if (cleaned) return;
    if (event && !['max-height', 'opacity'].includes(event.propertyName)) {
      return;
    }
    cleaned = true;
    delete element.dataset.animating;
    element.style.transition = previousTransition;
    element.style.overflow = previousOverflow;
    element.style.maxHeight = '';
    element.style.opacity = '';
    element.removeEventListener('transitionend', cleanup);
  };

  element.addEventListener('transitionend', cleanup);
  setTimeout(cleanup, duration + 100);
}

export function smoothCollapse(element, duration = 250) {
  if (!element) {
    return;
  }

  delete element.dataset.expanded;

  if (prefersReducedMotion()) {
    element.classList.add('hidden');
    element.style.maxHeight = '';
    element.style.opacity = '';
    return;
  }

  element.dataset.animating = '1';

  const previousTransition = element.style.transition;
  const previousOverflow = element.style.overflow;

  element.style.transition = `max-height ${duration}ms ease, opacity ${duration}ms ease`;
  element.style.overflow = 'hidden';
  element.style.maxHeight = `${element.scrollHeight}px`;
  element.style.opacity = '1';

  requestAnimationFrame(() => {
    element.style.maxHeight = '0px';
    element.style.opacity = '0';
  });

  let cleaned = false;
  const cleanup = (event) => {
    if (cleaned) return;
    if (event && !['max-height', 'opacity'].includes(event.propertyName)) {
      return;
    }
    cleaned = true;
    delete element.dataset.animating;
    element.classList.add('hidden');
    element.style.transition = previousTransition;
    element.style.overflow = previousOverflow;
    element.style.maxHeight = '';
    element.style.opacity = '';
    element.removeEventListener('transitionend', cleanup);
  };

  element.addEventListener('transitionend', cleanup);
  setTimeout(cleanup, duration + 100);
}

export function toggleSmooth(element, { duration = 250 } = {}) {
  if (!element) {
    return;
  }

  if (element.classList.contains('hidden')) {
    smoothExpand(element, duration);
  } else {
    smoothCollapse(element, duration);
  }
}

export function resizeContainer(element) {
  if (!element) {
    return;
  }
  // Skip during smooth expand/collapse animations to avoid conflicts
  if (element.dataset.animating) {
    return;
  }
  if (typeof window.__resizeContainer === 'function') {
    window.__resizeContainer(element);
  } else {
    // Allow form to grow naturally instead of constraining with scrollHeight
    element.style.maxHeight = 'none';
  }
}
